/**
 * The TCF Canada centre where the learner will sit the exam (Alliance française centres in
 * Cameroon, from the versioned format table): the urgent session mock exams of that centre reach it.
 */

import {
  type Learner,
  getAcademyMeRetrieveQueryKey,
  getAcademyUrgentListQueryKey,
  useAcademyFormatRetrieve,
  useAcademyMePartialUpdate,
} from "@agencyos/api-client";
import { Button, GlassCard, SelectField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { MapPin } from "lucide-react";
import { type FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { fieldErrors } from "../lib/settings";
import { type ExamCenter, parseCenters } from "./progressTypes";
import { CardTitle } from "./ui";

export function useCenters(): ExamCenter[] {
  const format = useAcademyFormatRetrieve(undefined, { query: { staleTime: 3_600_000, retry: false } });
  const centers = format.data?.status === 200 ? format.data.data.centers : undefined;
  return useMemo(() => parseCenters(centers), [centers]);
}

/** Options of the select: the value saved is the centre's city (what the server keeps). */
export function centerOptions(centers: ExamCenter[], chooseLabel: string, current?: string) {
  const options = centers.map((c) => ({ value: c.city, label: c.name || c.city }));
  if (current && !centers.some((c) => c.city === current)) options.unshift({ value: current, label: current });
  return [{ value: "", label: chooseLabel }, ...options];
}

/** A small card: choose and save the centre (shown while it is empty). */
export function CenterCard({ me }: { me: Learner }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const centers = useCenters();
  const save = useAcademyMePartialUpdate();
  const [center, setCenter] = useState(me.exam_center ?? "");
  const errors = fieldErrors(save.error);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(
      { data: { exam_center: center } },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getAcademyMeRetrieveQueryKey() });
          void queryClient.invalidateQueries({ queryKey: getAcademyUrgentListQueryKey() });
        },
      },
    );
  };

  return (
    <GlassCard as="section" className="ac-card ac-card--accent" aria-labelledby="academy-center">
      <form className="ac-form" onSubmit={onSubmit}>
        <CardTitle id="academy-center" icon={<MapPin size={16} />} tone="info">
          {t("academy.center.title")}
        </CardTitle>
        <p className="ac-secondary">{t("academy.center.invite")}</p>
        <SelectField
          label={t("academy.center.label")}
          value={center}
          onChange={(e) => setCenter(e.target.value)}
          options={centerOptions(centers, t("academy.goals.choose"), me.exam_center)}
          error={errors.exam_center}
        />
        {save.isError && !Object.keys(errors).length && <p className="ko">{t("common.error")}</p>}
        <div className="ac-card__actions">
          <Button type="submit" disabled={!center || save.isPending}>
            {save.isPending ? t("common.loading") : t("academy.center.save")}
          </Button>
        </div>
      </form>
    </GlassCard>
  );
}
